import { useEffect, useState } from 'react';
import {
  X, Key, Info, Building2, Users, User as UserIcon,
} from '../../icons';
import {
  getFileDetail, getFileActivity, listShares, getFileUsage, formatBytes,
  type NodePublic, type FileDetail, type ActivityItem, type SharesResponse,
  type UsageReport,
} from '../../api/workspace';
import { tWorkspace, type WorkspaceI18nKey } from '../../lib/workspace-i18n';
import { usageLabelOf, usageMetaOf } from './workspaceUsageMeta';
import { kindOfMime } from '../../lib/file-kind';
import { WorkspaceFileIcon } from './WorkspaceFileIcon';
import { formatWorkspaceDateTime } from './workspace-datetime';

/**
 * 우측 세부정보 패널 (2026-08-14) — 사용자 요구:
 *
 *   "세부정보를 누르면 우측 상세보기 패널이 나오고 … 아무것도 선택안되어있으면 내 파일에
 *    이력정보, 파일이나 폴더를 체크하면 엑서스 정보 및 엑서스 관리, 활동이력, 형식,
 *    수정날짜, 만든날짜, 경로, 크기가 나온다."
 *
 * 선택이 없을 때도 **빈 패널을 보여 주지 않는다** — 지금 폴더의 최근 활동을 대신 띄운다.
 * 빈 상태로 두면 세부정보 버튼을 눌러 놓고 "왜 아무것도 없지" 가 된다.
 *
 * ⚠ 이 패널은 문서 미리보기(`WorkspaceDocPanel`)와 **다른 자리**다. 저쪽은 파일을 여는
 *   자리이고 여기는 메타데이터를 읽는 자리다. 둘이 동시에 뜨면 폭이 모자라므로 호출부가
 *   하나만 열도록 조정한다.
 */

const EVENT_LABEL: Record<string, WorkspaceI18nKey> = {
  'workspace.file.uploaded': 'workspace.act.uploaded',
  'workspace.file.replaced': 'workspace.act.saved',
  'workspace.file.renamed': 'workspace.act.renamed',
  'workspace.file.moved': 'workspace.act.moved',
  'workspace.file.copied': 'workspace.act.copied',
  'workspace.file.deleted': 'workspace.act.deleted',
  'workspace.file.restored': 'workspace.act.restored',
  'workspace.file.shared': 'workspace.act.shared',
  'workspace.file.unshared': 'workspace.act.unshared',
  'workspace.folder.created': 'workspace.act.folder_created',
  'workspace.folder.renamed': 'workspace.act.renamed',
  'workspace.folder.moved': 'workspace.act.moved',
  'workspace.folder.deleted': 'workspace.act.deleted',
};

function when(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return formatWorkspaceDateTime(d);
}

interface Props {
  /** 선택된 단일 항목. null 이면 폴더 요약을 보여 준다. */
  node: NodePublic | null;
  /** 선택이 여러 개일 때의 개수 — 상세는 하나일 때만 뜻이 선다. */
  selectedCount: number;
  onClose: () => void;
  onManageAccess: (node: NodePublic) => void;
}

export function WorkspaceDetailPanel({ node, selectedCount, onClose, onManageAccess }: Props) {
  const [detail, setDetail] = useState<FileDetail | null>(null);
  const [activity, setActivity] = useState<ActivityItem[]>([]);
  const [shares, setShares] = useState<SharesResponse | null>(null);
  const [usage, setUsage] = useState<UsageReport | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!node) { setDetail(null); setActivity([]); setShares(null); setUsage(null); return; }
    let cancelled = false;
    setError(null);
    setDetail(null);
    void (async () => {
      try {
        // 셋을 병렬로 — 순차로 부르면 패널이 세 번에 걸쳐 채워져 깜빡인다.
        const [d, a, s, u] = await Promise.all([
          getFileDetail(node.id),
          getFileActivity(node.id, 20),
          listShares(node.id).catch(() => null),
          // 폴더는 첨부로 참조될 수 없다 — 조회를 아낀다.
          node.type === 'folder' ? Promise.resolve(null) : getFileUsage(node.id).catch(() => null),
        ]);
        if (cancelled) return;
        setDetail(d);
        setActivity(a);
        setShares(s);
        setUsage(u);
      } catch (err) {
        if (!cancelled) setError((err as any)?.response?.data?.message ?? (err as Error).message);
      }
    })();
    return () => { cancelled = true; };
  }, [node]);

  /** 전체 경로 — 한 줄로 잘라 보여 주고 tooltip 으로 전부 보여 준다. */
  const pathText = [
    tWorkspace('workspace.title'),
    ...(detail?.path ?? []).map((p) => p.name),
  ].join(' / ');

  const title = node
    ? node.name
    : tWorkspace(selectedCount > 1 ? 'workspace.detail.multi' : 'workspace.nav.my');

  return (
    <aside className="ws-detail" aria-label={tWorkspace('workspace.detail.aria')}>
      <div className="ws-detail-hd">
        {node && <WorkspaceFileIcon node={node} size={18} />}
        <b title={title}>{title}</b>
        <button
          type="button"
          className="ws-detail-close"
          onClick={onClose}
          aria-label={tWorkspace('workspace.detail.close')}
        >
          <X size={16} />
        </button>
      </div>

      <div className="ws-detail-body">
        {error && <div className="workspace-modal-error" role="alert">{error}</div>}

        {/* 선택 없음 · 여러 개 — 상세 대신 안내. */}
        {!node && (
          <div className="ws-detail-empty">
            <Info size={30} aria-hidden />
            <p>
              {selectedCount > 1
                ? tWorkspace('workspace.detail.multi_hint', { count: selectedCount })
                : tWorkspace('workspace.detail.empty_hint')}
            </p>
          </div>
        )}

        {node && (
          <>
            {node.type !== 'folder' && (
              <section className="ws-detail-sec">
                <h4>{tWorkspace('workspace.detail.usage')}</h4>
                {usage && usage.total === 0 && (
                  <p className="ws-detail-note">{tWorkspace('workspace.detail.usage_none')}</p>
                )}
                {usage && usage.refs.map((r) => {
                  // 모르는 종류(서버가 새로 켠 출처)도 그려져야 한다 — workspaceUsageMeta 참고.
                  const meta = usageMetaOf(r.kind);
                  const Icon = meta.icon;
                  return (
                    <div key={`${r.kind}-${r.id}`} className="ws-usage-ref">
                      <Icon size={14} aria-hidden />
                      <span className="ws-usage-ref-txt">
                        <b title={r.label}>{r.label}</b>
                        <span>
                          {usageLabelOf(r.kind)}
                          {r.sublabel ? ` · ${r.sublabel}` : ''}
                        </span>
                      </span>
                    </div>
                  );
                })}
              </section>
            )}

            <section className="ws-detail-sec">
              <h4>{tWorkspace('workspace.detail.access')}</h4>
              {shares?.owner && (
                <div className="ws-who">
                  <span className="ws-avatar"><UserIcon size={13} aria-hidden /></span>
                  <span className="ws-who-txt">
                    {/* 이름은 한 줄로 자른다 — 전체는 마우스를 올려 본다(2026-08-18). */}
                    <b title={shares.owner.name}>{shares.owner.name}</b>
                    <span>{tWorkspace('workspace.detail.owner')}</span>
                  </span>
                </div>
              )}
              {shares?.shares.map((s) => (
                <div key={s.id} className="ws-who">
                  <span className="ws-avatar">
                    {s.targetType === 'company'
                      ? <Building2 size={13} aria-hidden />
                      : s.targetType === 'team'
                        ? <Users size={13} aria-hidden />
                        : <UserIcon size={13} aria-hidden />}
                  </span>
                  <span className="ws-who-txt">
                    <b title={s.targetName ?? undefined}>
                      {s.targetName ?? tWorkspace('workspace.detail.unknown_target')}
                    </b>
                    <span>
                      {tWorkspace(s.targetType === 'company'
                        ? 'workspace.share.scope_company'
                        : s.targetType === 'team'
                          ? 'workspace.share.scope_team'
                          : 'workspace.share.scope_user')}
                    </span>
                  </span>
                  <span className="ws-who-perm">
                    {tWorkspace(s.permission === 'EDIT' ? 'workspace.share.perm_edit' : 'workspace.share.perm_view')}
                  </span>
                </div>
              ))}
              {/* 상위 폴더에서 받은 공유 (2026-10-27) — 「누가 볼 수 있나」에 함께 보여야 한다. */}
              {shares?.inherited?.map((s) => (
                <div key={`inh-${s.id}`} className="ws-who">
                  <span className="ws-avatar">
                    {s.targetType === 'company'
                      ? <Building2 size={13} aria-hidden />
                      : s.targetType === 'team'
                        ? <Users size={13} aria-hidden />
                        : <UserIcon size={13} aria-hidden />}
                  </span>
                  <span className="ws-who-txt">
                    <b title={s.targetName ?? undefined}>
                      {s.targetName ?? tWorkspace('workspace.detail.unknown_target')}
                    </b>
                    <span>{tWorkspace('workspace.share.inherited_from', { name: s.fromName })}</span>
                  </span>
                  <span className="ws-who-perm">
                    {tWorkspace(s.permission === 'EDIT' ? 'workspace.share.perm_edit' : 'workspace.share.perm_view')}
                  </span>
                </div>
              ))}
              {shares && shares.shares.length === 0 && (shares.inherited?.length ?? 0) === 0 && (
                <p className="ws-detail-note">{tWorkspace('workspace.detail.no_shares')}</p>
              )}
              {shares?.canManage && (
                <button
                  type="button"
                  className="ws-detail-action"
                  onClick={() => onManageAccess(node)}
                >
                  <Key size={14} aria-hidden /> {tWorkspace('workspace.row.access')}
                </button>
              )}
            </section>

            <section className="ws-detail-sec">
              <h4>{tWorkspace('workspace.detail.activity')}</h4>
              {activity.length === 0 && (
                <p className="ws-detail-note">{tWorkspace('workspace.detail.no_activity')}</p>
              )}
              <div className="ws-timeline">
                {activity.map((a) => (
                  <div key={a.id} className="ws-tl-item">
                    <span className="ws-tl-dot" />
                    <span className="ws-tl-txt">
                      <span>
                        {a.actorName ? <b>{a.actorName}</b> : null}
                        {a.actorName ? ' ' : ''}
                        {tWorkspace(EVENT_LABEL[a.eventType] ?? 'workspace.act.other')}
                      </span>
                      <em>{when(a.createdAt)}</em>
                    </span>
                  </div>
                ))}
              </div>
            </section>

            <section className="ws-detail-sec">
              <h4>{tWorkspace('workspace.detail.props')}</h4>
              <dl className="ws-kv">
                <dt>{tWorkspace('workspace.detail.format')}</dt>
                <dd>
                  {node.type === 'folder'
                    ? tWorkspace('workspace.create.folder')
                    : tWorkspace(`workspace.kind.${kindOfMime(node.mimeType)}` as WorkspaceI18nKey)}
                </dd>
                <dt>{tWorkspace('workspace.col.modified')}</dt>
                <dd className="ws-num">{node.updatedAt ? when(node.updatedAt) : '—'}</dd>
                <dt>{tWorkspace('workspace.detail.created')}</dt>
                <dd className="ws-num">{when(node.createdAt)}</dd>
                <dt>{tWorkspace('workspace.detail.path')}</dt>
                {/*
                  경로는 **한 줄**로 자른다 (2026-08-18). 폴더 이름이 길면 여기서 서너 줄로
                  늘어져 패널에 스크롤이 생겼다. 전체 경로는 마우스를 올려 본다.
                */}
                <dd className="ws-kv-path" title={pathText}>{pathText}</dd>
                <dt>{tWorkspace('workspace.col.size')}</dt>
                <dd className="ws-num">
                  {node.type === 'folder' ? '—' : formatBytes(node.sizeBytes)}
                </dd>
              </dl>
            </section>
          </>
        )}
      </div>
    </aside>
  );
}
